package interviewprep.client

import interviewprep.common.FeedbackDto
import interviewprep.common.InterviewPromptText
import interviewprep.common.PracticeMode
import interviewprep.common.QuestionPrompt
import interviewprep.common.SessionSnapshotV1
import interviewprep.common.TurnTransitionHint
import interviewprep.common.VoiceQuestion
import kotlinx.browser.localStorage
import kotlinx.serialization.Serializable
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

internal val webJson = Json {
    encodeDefaults = true
    ignoreUnknownKeys = true
}

internal const val USER_ID_KEY = "voiceInterview.web.userId"
internal const val SESSION_ID_KEY = "voiceInterview.web.sessionId"
internal const val SESSION_SNAPSHOT_KEY = "voiceInterview.web.sessionSnapshot"
internal const val TIMELINE_KEY = "voiceInterview.web.timeline"
internal const val TRACK_KEY = "voiceInterview.web.track"
internal const val PRACTICE_MODE_KEY = "voiceInterview.web.practiceMode"

private const val LEGACY_USER_ID_KEY = "loopPass.web.userId"
private const val LEGACY_SESSION_ID_KEY = "loopPass.web.sessionId"
private const val LEGACY_SESSION_SNAPSHOT_KEY = "loopPass.web.sessionSnapshot"
private const val LEGACY_TIMELINE_KEY = "loopPass.web.timeline"
private const val LEGACY_TRACK_KEY = "loopPass.web.track"
private const val LEGACY_PRACTICE_MODE_KEY = "loopPass.web.practiceMode"

internal fun storedValue(key: String, legacyKey: String): String? {
    val current = localStorage.getItem(key)?.takeIf { it.isNotBlank() }
    if (current != null) return current
    val legacy = localStorage.getItem(legacyKey)?.takeIf { it.isNotBlank() } ?: return null
    localStorage.setItem(key, legacy)
    return legacy
}

@Serializable
internal data class TimelineEntry(
    val role: TimelineRole,
    val text: String,
    val detail: String? = null,
    val score: Int? = null
)

@Serializable
internal enum class TimelineRole {
    INTERVIEWER,
    CANDIDATE,
    COACH,
    SYSTEM
}

internal data class TrackOption(
    val value: String,
    val label: String,
    val description: String
)

internal val trackOptions = listOf(
    TrackOption("SYSTEM_DESIGN", "System design", "Architecture tradeoffs, scale, and reliability."),
    TrackOption("BEHAVIORAL", "Behavioral", "STAR-style stories with concrete impact."),
    TrackOption("CODING", "Coding", "Problem-solving narration and implementation clarity."),
    TrackOption("TECHNICAL_DEPTH", "Technical depth", "Deep dives into specific systems and technologies."),
    TrackOption("LLD", "Low-level design", "Classes, APIs, data models, and boundaries."),
    TrackOption("CAREER", "Career story", "Work-history and leadership narrative practice.")
)

internal fun loadOrCreateUserId(): String {
    val existing = storedValue(USER_ID_KEY, LEGACY_USER_ID_KEY)
    if (existing != null) return existing
    val created = "web-${buildRequestId()}"
    localStorage.setItem(USER_ID_KEY, created)
    return created
}

internal fun persistSessionSnapshot(sessionId: String, snapshot: SessionSnapshotV1) {
    localStorage.setItem(SESSION_ID_KEY, sessionId)
    localStorage.setItem(SESSION_SNAPSHOT_KEY, webJson.encodeToString(snapshot))
}

internal fun loadSessionSnapshot(): SessionSnapshotV1? =
    storedValue(SESSION_SNAPSHOT_KEY, LEGACY_SESSION_SNAPSHOT_KEY)
        ?.let { raw -> runCatching { webJson.decodeFromString<SessionSnapshotV1>(raw) }.getOrNull() }

internal fun loadTimeline(): List<TimelineEntry> =
    storedValue(TIMELINE_KEY, LEGACY_TIMELINE_KEY)
        ?.let { raw -> runCatching { webJson.decodeFromString<List<TimelineEntry>>(raw) }.getOrNull() }
        ?: emptyList()

internal fun loadSelectedTrack(): String =
    storedValue(TRACK_KEY, LEGACY_TRACK_KEY) ?: "SYSTEM_DESIGN"

internal fun loadPracticeMode(): PracticeMode =
    runCatching {
        PracticeMode.valueOf(storedValue(PRACTICE_MODE_KEY, LEGACY_PRACTICE_MODE_KEY) ?: PracticeMode.STANDARD.name)
    }.getOrDefault(PracticeMode.STANDARD)

internal fun loadSessionId(): String? =
    storedValue(SESSION_ID_KEY, LEGACY_SESSION_ID_KEY)

internal fun persistTimeline(entries: List<TimelineEntry>) {
    localStorage.setItem(TIMELINE_KEY, webJson.encodeToString(entries))
}

internal fun toVoiceQuestion(question: QuestionPrompt): VoiceQuestion =
    VoiceQuestion(
        questionId = question.questionId,
        title = question.title,
        voicePrompt = question.prompt,
        displayPrompt = cleanInterviewText(raw = question.prompt, title = question.title),
        sourcePath = question.sourcePath
    )

internal fun questionDisplayText(question: VoiceQuestion?): String =
    question?.displayPrompt?.let { cleanInterviewText(raw = it, title = question.title) }?.takeIf { it.isNotBlank() }
        ?: question?.voicePrompt?.let { cleanInterviewText(raw = it, title = question.title) }
        ?: "Choose a round and start when you are ready."

internal fun questionSpeechText(question: VoiceQuestion?): String? =
    question?.voicePrompt?.let { cleanInterviewText(raw = it, title = question.title) }?.takeIf { it.isNotBlank() }
        ?: question?.displayPrompt?.let { cleanInterviewText(raw = it, title = question.title) }?.takeIf { it.isNotBlank() }

internal fun cleanInterviewText(raw: String, title: String? = null): String =
    InterviewPromptText.displayText(raw = raw, title = title)

internal fun FeedbackDto.toTimelineEntry(transitionHint: TurnTransitionHint?): TimelineEntry {
    val coaching = mentorCoaching
    val text =
        mentorSpeech?.trim()?.takeIf { it.isNotBlank() }
            ?: buildString {
                append("Strengths: ")
                append(evaluation.strengths.joinToString("; ").ifBlank { "none yet" })
                append("\n\nGaps: ")
                append(evaluation.gaps.joinToString("; ").ifBlank { "none called out" })
                if (coaching != null) {
                    append("\n\nNext: ")
                    append(coaching.nextAttemptGuidance)
                }
            }
    val transition =
        when (transitionHint ?: transition) {
            TurnTransitionHint.NEXT_QUESTION -> "Next question"
            TurnTransitionHint.DEEPEN_CURRENT -> "Deepen current answer"
            TurnTransitionHint.INTERVIEW_COMPLETE -> "Interview complete"
        }
    return TimelineEntry(
        role = TimelineRole.COACH,
        text = text,
        detail = transition,
        score = evaluation.score
    )
}

internal fun TimelineRole.label(): String =
    when (this) {
        TimelineRole.INTERVIEWER -> "Interviewer"
        TimelineRole.CANDIDATE -> "You"
        TimelineRole.COACH -> "Coach"
        TimelineRole.SYSTEM -> "System"
    }
